import { useState, useCallback, useEffect } from 'react'

export interface SelectedElement {
  description: string
  textContent: string
  tagName: string
  rect: DOMRect
  elementType: string
}

export interface UseElementSelector {
  isSelecting: boolean
  hoveredRect: DOMRect | null
  selectedElement: SelectedElement | null
  startSelecting: () => void
  stopSelecting: () => void
  clearSelection: () => void
}

export function useElementSelector(): UseElementSelector {
  const [isSelecting, setIsSelecting] = useState(false)
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null)
  const [selectedElement, setSelectedElement] =
    useState<SelectedElement | null>(null)
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null)

  // Expose setSelectedElement globally so AppLayout can set it from widget events
  useEffect(() => {
    ;(window as any).__juaSetSelectedElement = setSelectedElement
    return () => {
      delete (window as any).__juaSetSelectedElement
    }
  }, [setSelectedElement])

  const startSelecting = useCallback(() => {
    setIsSelecting(true)
    setSelectedElement(null)
  }, [])

  const stopSelecting = useCallback(() => {
    setIsSelecting(false)
    setHoveredRect(null)
    setHoveredElement(null)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedElement(null)
  }, [])

  useEffect(() => {
    if (!isSelecting) return

    const handleMouseMove = (e: MouseEvent) => {
      // Ignore the overlay itself if it gets in the way (handled via pointer-events usually)
      // We want to find the deepest element under the cursor that is "interesting"
      const target = document.elementFromPoint(
        e.clientX,
        e.clientY,
      ) as HTMLElement

      if (!target || target.id === 'element-selector-overlay') return

      // Logic to find a meaningful container
      // We look for specific classes, roles, or semantic tags
      const interestingSelector =
        'article, section, [role="widget"], .card, button, input, .leaflet-container'
      const element = target.closest(interestingSelector) || target

      if (element instanceof HTMLElement) {
        setHoveredElement(element)
        setHoveredRect(element.getBoundingClientRect())
      }
    }

    const handleClick = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (hoveredElement) {
        const rect = hoveredElement.getBoundingClientRect()

        // Infer type
        let type = 'element'
        if (hoveredElement.tagName === 'BUTTON') type = 'button'
        else if (hoveredElement.tagName === 'INPUT') type = 'input'
        else if (hoveredElement.closest('.leaflet-container')) type = 'map'
        else if (hoveredElement.closest('[role="widget"]')) type = 'widget'
        else if (hoveredElement.tagName === 'SECTION') type = 'section'

        // Get description
        let desc = type
        const ariaLabel = hoveredElement.getAttribute('aria-label')
        const title = hoveredElement.getAttribute('title')
        const heading = hoveredElement.querySelector(
          'h1, h2, h3, h4, h5, h6',
        )?.textContent

        if (ariaLabel) desc = ariaLabel
        else if (title) desc = title
        else if (heading) desc = heading
        else if (hoveredElement.innerText) {
          desc =
            hoveredElement.innerText.slice(0, 30).replace(/\n/g, ' ') +
            (hoveredElement.innerText.length > 30 ? '...' : '')
        }

        setSelectedElement({
          description: desc || 'Selected Element',
          textContent: hoveredElement.innerText?.slice(0, 200) || '',
          tagName: hoveredElement.tagName.toLowerCase(),
          rect,
          elementType: type,
        })

        stopSelecting()
      }
    }

    // Add a global style to change cursor
    document.body.style.cursor = 'crosshair'

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('click', handleClick, { capture: true })

    return () => {
      document.body.style.cursor = ''
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('click', handleClick, { capture: true })
    }
  }, [isSelecting, hoveredElement, stopSelecting])

  return {
    isSelecting,
    hoveredRect,
    selectedElement,
    startSelecting,
    stopSelecting,
    clearSelection,
  }
}
